// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  LocalStorageValue,
  getLocalStorageValue,
  removeLocalStorageValue,
  setLocalStorageValue,
} from '@/utilities/local-storage';
import { isEqual } from 'lodash-es';
import { useCallback, useRef, useSyncExternalStore } from 'react';
import { z } from 'zod';

type UseLocalStorage<Schema extends z.ZodTypeAny> = [
  LocalStorageValue<Schema> | null,
  (value: unknown) => void,
];

/**
 * Adds a storage event listener with the provided callback. This function
 * returns a function that can be called to remove the event listener.
 *
 * @param callback - The callback to call when a storage event occurs.
 */
function subscribe(callback: () => void) {
  window.addEventListener('storage', callback);

  return () => {
    window.removeEventListener('storage', callback);
  };
}

/**
 * A React hook that manages a single value in local storage.
 *
 * @param key - The key to use in local storage.
 * @param schema - The schema to use to parse the value.
 */
export function useLocalStorage<Schema extends z.ZodTypeAny>(
  key: string,
  schema: Schema
): UseLocalStorage<Schema> {
  const snapshot = useRef(getLocalStorageValue(key, schema));

  const getSnapshot = () => {
    const value = getLocalStorageValue(key, schema);

    if (isEqual(value, snapshot.current)) {
      // Zod schemas used in this way can't infer the type of the parsed value,
      // so this gets flagged by ESLint even though it's safe.
      //
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return snapshot.current;
    }

    snapshot.current = value;

    // Zod schemas used in this way can't infer the type of the parsed value,
    // so this gets flagged by ESLint even though it's safe.
    //
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return value;
  };

  const store = useSyncExternalStore(subscribe, getSnapshot);

  const setValue = useCallback(
    (value: LocalStorageValue<Schema> | null) => {
      if (value === undefined || value === null) {
        removeLocalStorageValue(key);
        return;
      }

      setLocalStorageValue(key, value);
    },
    [key]
  );

  return [store, setValue];
}
